import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, ChevronRight, ChevronLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Workers Tab
// ---------------------------------------------------------------------------

const WorkersTab = ({ workers, jobs, payments, onRefresh }) => {
  const { getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [editWorker, setEditWorker] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', notes: '' });

  const openAdd = () => {
    setEditWorker(null);
    setForm({ name: '', phone: '', notes: '' });
    setIsOpen(true);
  };

  const openEdit = (w) => {
    setEditWorker(w);
    setForm({ name: w.name, phone: w.phone || '', notes: w.notes || '' });
    setIsOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t('workerNameRequired')); return; }
    try {
      if (editWorker) {
        await axios.put(`${API}/outsourcing/workers/${editWorker.id}`, form, { headers: getAuthHeader() });
        toast.success(t('workerUpdated'));
      } else {
        await axios.post(`${API}/outsourcing/workers`, form, { headers: getAuthHeader() });
        toast.success(t('workerAdded'));
      }
      setIsOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save worker');
    }
  };

  const handleDelete = async (w) => {
    if (!window.confirm(`Delete worker "${w.name}"? This will fail if they have assigned jobs.`)) return;
    try {
      await axios.delete(`${API}/outsourcing/workers/${w.id}`, { headers: getAuthHeader() });
      toast.success(t('workerDeleted'));
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete worker');
    }
  };

  const workerSummary = (workerId) => {
    const totalWork = jobs.filter(j => j.worker_id === workerId && j.status === 'completed').reduce((s, j) => s + (j.total_cost || 0), 0);
    const totalPaid = payments.filter(p => p.worker_id === workerId).reduce((s, p) => s + (p.amount || 0), 0);
    return { totalWork, totalPaid, balance: totalWork - totalPaid };
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
          {workers.length} {t('workers').toLowerCase()}
        </p>
        <Button size="sm" onClick={openAdd}>
          <Plus className="w-4 h-4 me-1" />
          <span lang={language === 'ar' ? 'ar' : 'en'}>{t('addWorker')}</span>
        </Button>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</th>
              <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('phone')}</th>
              <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('totalWorkEgp')}</th>
              <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('totalPaidEgp')}</th>
              <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('balanceEgp')}</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {workers.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                  {t('noWorkersYet')}
                </td>
              </tr>
            )}
            {workers.map(w => {
              const { totalWork, totalPaid, balance } = workerSummary(w.id);
              return (
                <tr key={w.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="p-3">
                    <button
                      onClick={() => navigate(`/outsourcing/worker/${w.id}`)}
                      className="font-medium text-primary hover:underline flex items-center gap-1"
                    >
                      <span lang={language === 'ar' ? 'ar' : 'en'}>{w.name}</span>
                      {language === 'ar'
                        ? <ChevronLeft className="w-3 h-3" />
                        : <ChevronRight className="w-3 h-3" />}
                    </button>
                  </td>
                  <td className="p-3 text-muted-foreground">{w.phone || '—'}</td>
                  <td className="p-3 text-end">{totalWork.toFixed(2)}</td>
                  <td className="p-3 text-end text-green-700">{totalPaid.toFixed(2)}</td>
                  <td className={`p-3 text-end font-medium ${balance > 0 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                    {balance.toFixed(2)}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(w)}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(w)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>
              {editWorker ? t('editWorker') : t('addWorker')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('name')} *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('phone')}</Label>
              <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('notes')}</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIsOpen(false)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleSave} lang={language === 'ar' ? 'ar' : 'en'}>
                {editWorker ? t('saveChanges') : t('addWorker')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Rates Tab (global per item_code)
// ---------------------------------------------------------------------------

const RatesTab = ({ rates, orders, onRefresh }) => {
  const { getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [editRate, setEditRate] = useState(null);
  const [form, setForm] = useState({ item_code: '', cost_per_piece: '' });

  const knownItemCodes = [...new Set(orders.map(o => o.item_code).filter(Boolean))];

  const openAdd = () => {
    setEditRate(null);
    setForm({ item_code: '', cost_per_piece: '' });
    setIsOpen(true);
  };

  const openEdit = (r) => {
    setEditRate(r);
    setForm({ item_code: r.item_code, cost_per_piece: r.cost_per_piece });
    setIsOpen(true);
  };

  const handleSave = async () => {
    if (!form.item_code.trim()) { toast.error(t('itemCodeRequired')); return; }
    if (!form.cost_per_piece || isNaN(form.cost_per_piece)) { toast.error(t('enterValidCost')); return; }
    try {
      if (editRate) {
        await axios.put(`${API}/outsourcing/rates/${editRate.id}`, { item_code: form.item_code, cost_per_piece: parseFloat(form.cost_per_piece) }, { headers: getAuthHeader() });
        toast.success(t('rateUpdated'));
      } else {
        await axios.post(`${API}/outsourcing/rates`, { item_code: form.item_code, cost_per_piece: parseFloat(form.cost_per_piece) }, { headers: getAuthHeader() });
        toast.success(t('rateSaved'));
      }
      setIsOpen(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rate');
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete rate for "${r.item_code}"?`)) return;
    try {
      await axios.delete(`${API}/outsourcing/rates/${r.id}`, { headers: getAuthHeader() });
      toast.success(t('rateDeleted'));
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete rate');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
          {rates.length} {t('rates').toLowerCase()} — {t('appliesToAllWorkers')}
        </p>
        <Button size="sm" onClick={openAdd}>
          <Plus className="w-4 h-4 me-1" />
          <span lang={language === 'ar' ? 'ar' : 'en'}>{t('addRate')}</span>
        </Button>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 border-b">
            <tr>
              <th className="text-start p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('itemCode')}</th>
              <th className="text-end p-3 font-medium" lang={language === 'ar' ? 'ar' : 'en'}>{t('costPerPiece')} ({t('currency')})</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rates.length === 0 && (
              <tr>
                <td colSpan={3} className="p-6 text-center text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
                  {t('noRatesYet')}
                </td>
              </tr>
            )}
            {rates.map(r => (
              <tr key={r.id} className="border-b last:border-0 hover:bg-muted/20">
                <td className="p-3 font-mono font-medium">{r.item_code}</td>
                <td className="p-3 text-end">{parseFloat(r.cost_per_piece).toFixed(2)}</td>
                <td className="p-3">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(r)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>
              {editRate ? t('editRate') : t('addRate')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('itemCode')} *</Label>
              <Input
                value={form.item_code}
                onChange={e => setForm(f => ({ ...f, item_code: e.target.value }))}
                placeholder="e.g. ABC-001"
                list="item-codes-list"
              />
              <datalist id="item-codes-list">
                {knownItemCodes.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('costPerPiece')} ({t('currency')}) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.cost_per_piece}
                onChange={e => setForm(f => ({ ...f, cost_per_piece: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setIsOpen(false)} lang={language === 'ar' ? 'ar' : 'en'}>
                {t('cancel')}
              </Button>
              <Button onClick={handleSave} lang={language === 'ar' ? 'ar' : 'en'}>
                {editRate ? t('saveChanges') : t('addRate')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Outsourcing Page
// ---------------------------------------------------------------------------

export const Outsourcing = () => {
  const { getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const [workers, setWorkers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [rates, setRates] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [wRes, jRes, pRes, rRes, oRes] = await Promise.all([
        axios.get(`${API}/outsourcing/workers`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/jobs`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/payments`, { headers: getAuthHeader() }),
        axios.get(`${API}/outsourcing/rates`, { headers: getAuthHeader() }),
        axios.get(`${API}/orders`, { headers: getAuthHeader() }),
      ]);
      setWorkers(wRes.data);
      setJobs(jRes.data);
      setPayments(pRes.data);
      setRates(rRes.data);
      setOrders(oRes.data);
    } catch {
      toast.error('Failed to load outsourcing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) return (
    <div className="p-8 text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>
      {t('loading')}
    </div>
  );

  return (
    <div className="p-8">
      <h1 className="text-4xl font-bold tracking-tight mb-8" lang={language === 'ar' ? 'ar' : 'en'}>
        {t('outsourcing')}
      </h1>
      <Tabs defaultValue="workers">
        <TabsList className="mb-6">
          <TabsTrigger value="workers" lang={language === 'ar' ? 'ar' : 'en'}>{t('workers')}</TabsTrigger>
          <TabsTrigger value="rates" lang={language === 'ar' ? 'ar' : 'en'}>{t('rates')}</TabsTrigger>
        </TabsList>

        <TabsContent value="workers">
          <WorkersTab workers={workers} jobs={jobs} payments={payments} onRefresh={loadData} />
        </TabsContent>

        <TabsContent value="rates">
          <RatesTab rates={rates} orders={orders} onRefresh={loadData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
